import "./env"; // FOUND-09 — Zod validation fires at module load; throws on invalid/missing env.

import { createDefaultQueryClient } from "@linkhr/api-client";
import { finishMicrosoftSignInPopup } from "@linkhr/auth";
import { createAppRoot } from "@linkhr/auth/react";
import { AuthSplash } from "@linkhr/auth/pages";
import { initI18n } from "@linkhr/i18n";
import { ErrorScreen } from "@linkhr/design-system";
import { routeTree } from "./routes";
import { env } from "./env";
import { resumeSignedInSession } from "./resumeSession";
import "./styles/app.css";

/**
 * Entry — the global login's bootstrap.
 *
 * The same `createAppRoot` sequence the other three apps run, minus the parts
 * a login funnel has no use for: no MSW (one backend call, and mocking it
 * mocks away the feature), no billing banner, no breadcrumb augmentation.
 *
 * `aud: "admin"` here is only the STARTING audience — the app's boot-time
 * network config and the audience `<AuthProvider>` publishes. The login itself
 * decides per attempt and re-points the network config on a refusal; see
 * `audienceProbe.ts`.
 *
 * A visitor who is already signed in is sent straight into their app instead
 * of the form (`resumeSession.ts`): the silent restore that `createAppRoot`
 * runs for `admin` is followed, if it found nothing, by the same restore for
 * `mobile`; a live session whose company is known is forwarded with
 * `location.replace` to `/admin/` or `/me/`, keeping a deep link that points
 * inside that app. The splash stays up throughout, so a signed-in visitor
 * never sees the form flash. No session, no company, or any failure → the
 * form, exactly as before.
 *
 * Loop guard: the two apps can disagree about whether a session exists, and
 * then they would bounce off each other forever. So a forward happens at most
 * once per short window in a tab (a sessionStorage timestamp — only a latch,
 * never a credential), and never when an app sent the visitor here with a
 * `reason`. Inside the guard they see the form, which costs one sign-in and
 * cannot loop.
 */
async function boot(): Promise<void> {
  await initI18n({
    backendLoadPath: `${import.meta.env.BASE_URL}locales/{{lng}}/{{ns}}.json`,
  });
  const queryClient = createDefaultQueryClient();
  await createAppRoot({
    aud: "admin",
    routeTree,
    apiBaseUrl: env.VITE_API_BASE_URL,
    SplashComponent: AuthSplash,
    queryClient,
    defaultRoute: "/",
    DefaultErrorComponent: ErrorScreen,
    afterRestore: (snapshot) => resumeSignedInSession(snapshot, env.VITE_API_BASE_URL),
  });
}

// The Microsoft sign-in popup returns to the origin root — this app on the
// `app.` host, whichever sign-in page opened it. That window only hands the
// response back to its opener and closes; booting here would start a second
// sign-in inside the popup.
if (!finishMicrosoftSignInPopup()) void boot();
